import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";


const nextSteps = [
  {
    number: "1",
    title: "We complete your final setup",
    description:
      "We’ll confirm your activation, complete your restaurant setup, and prepare your secure, easy-to-use TapToTable Dashboard access.",
  },
  {
    number: "2",
    title: "Your TapToTable Page goes live",
    description:
      "We’ll complete a final review of your restaurant information, links, selected Promotion, Event, or Featured Item, and customer experience before making your page available to customers.",
  },
  {
    number: "3",
    title: "You receive Dashboard access",
    description:
      "We’ll email your secure, easy-to-use TapToTable Dashboard access so you can create or update Promotions, Events, and Featured Items, manage restaurant details, change photos and links, manage customer actions, and monitor supported customer activity.",
  },
];

const includedItems = [
  "Your hosted TapToTable Page",
  "Secure, Easy-to-Use TapToTable Dashboard",
  "Create and update Promotions, Events, and Featured Items anytime",
  "Update restaurant details, photos, links, and customer actions",
  "Customer activity and supported analytics",
  "Restaurant photo hosting + TapToTable hosting",
  "Ongoing support",
  "Cancel anytime",
];


function ActivationInquiryForm({ requestType, onClose }) {
  const [form, setForm] = useState({
    restaurant_name: "",
    customer_name: "",
    email: "",
    message: "",
    website: "",
    form_started_at: new Date().toISOString(),
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const isLocations = requestType === "additional_locations";
  const title = isLocations
    ? "Ask About Additional Locations"
    : "Contact TapToTable Support";

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    let cancelled = false;
    let script = document.querySelector(
      'script[data-taptotable-turnstile="true"]'
    );

    const renderWidget = () => {
      if (
        cancelled ||
        !containerRef.current ||
        !window.turnstile ||
        widgetIdRef.current !== null
      ) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(
        containerRef.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "dark",
          callback: (token) => setTurnstileToken(token || ""),
          "expired-callback": () => setTurnstileToken(""),
          "error-callback": () => setTurnstileToken(""),
        }
      );
    };

    if (script) {
      if (window.turnstile) {
        renderWidget();
      } else {
        script.addEventListener("load", renderWidget);
      }
    } else {
      script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.taptotableTurnstile = "true";
      script.addEventListener("load", renderWidget);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (script) {
        script.removeEventListener("load", renderWidget);
      }
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already have been removed.
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the security check.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API}/customer-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          request_type: requestType,
          turnstile_token: turnstileToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          typeof result.detail === "string"
            ? result.detail
            : "Could not send your request."
        );
      }

      setSent(true);
    } catch (err) {
      setError(err.message || "Please try again.");
      setTurnstileToken("");

      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          // Widget may already be unavailable.
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle =
    "w-full rounded-xl border border-white/15 bg-[#1A1A1F] px-4 py-3 text-sm text-white placeholder-zinc-500 focus:border-[#00E5FF] focus:outline-none";

  return (
    <div className="mt-5 rounded-2xl border border-[#00E5FF]/30 bg-black/30 p-5 text-white">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-zinc-400 hover:text-white"
        >
          Close
        </button>
      </div>

      {sent ? (
        <div role="status" className="rounded-xl bg-[#00E5FF]/10 p-4">
          <p className="font-semibold text-[#00E5FF]">
            Request Sent Successfully
          </p>
          <p className="mt-2 text-sm text-zinc-300">
            TapToTable has received your request.
            Our team will review it and contact you.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-xs text-zinc-400">
              Restaurant Name
            </label>
            <input
              required
              maxLength={200}
              value={form.restaurant_name}
              onChange={(e) =>
                setForm({ ...form, restaurant_name: e.target.value })
              }
              className={fieldStyle}
              placeholder="Your restaurant name"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs text-zinc-400">
              Your Name
            </label>
            <input
              required
              minLength={2}
              maxLength={120}
              value={form.customer_name}
              onChange={(e) =>
                setForm({ ...form, customer_name: e.target.value })
              }
              className={fieldStyle}
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs text-zinc-400">
              Email Used During Checkout
            </label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
              className={fieldStyle}
              placeholder="owner@restaurant.com"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs text-zinc-400">
              {isLocations
                ? "Tell Us About Your Additional Locations"
                : "How Can We Help?"}
            </label>
            <textarea
              required
              minLength={10}
              maxLength={3000}
              rows={5}
              value={form.message}
              onChange={(e) =>
                setForm({ ...form, message: e.target.value })
              }
              className={fieldStyle}
              placeholder={
                isLocations
                  ? "How many locations? Include names and cities."
                  : "Describe what you need assistance with."
              }
            />
          </div>

          <div className="absolute left-[-10000px]" aria-hidden="true">
            <input
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) =>
                setForm({ ...form, website: e.target.value })
              }
            />
          </div>

          {TURNSTILE_SITE_KEY && (
            <div ref={containerRef} className="flex justify-center" />
          )}

          {error && (
            <p role="alert" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl bg-[#00E5FF] px-5 py-3 font-semibold text-black disabled:opacity-50"
          >
            {submitting ? "Sending..." : "Send Request"}
          </button>

          <p className="text-center text-xs text-zinc-500">
            Your request will be sent directly to TapToTable Support.
          </p>
        </form>
      )}
    </div>
  );
}

export default function ActivationSuccess() {
  
  const [activeInquiry, setActiveInquiry] = useState(null);

  return (
    <div
      className="min-h-screen bg-[#09090B] text-white"
      data-testid="activation-success-page"
    >
      <Navbar />

      <main className="px-6 pb-24 pt-32 md:px-12">
        <div className="mx-auto max-w-5xl">
          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="text-center"
          >
            <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/15 text-3xl font-bold text-[#00E5FF]">
              ✓
            </div>

            <div className="mt-6 text-[11px] font-bold uppercase tracking-[0.22em] text-[#00E5FF]">
              Welcome to TapToTable
            </div>

            <h1
              className="mx-auto mt-4 max-w-4xl font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="activation-success-heading"
            >
              Your TapToTable Activation Is Confirmed
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
              Thank you for choosing TapToTable. Your activation is confirmed,
              and we&apos;re completing the final setup for your restaurant.
              We&apos;ll email your secure, easy-to-use TapToTable Dashboard
              access and let you know as soon as your TapToTable Page is
              ready for customers.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-12 overflow-hidden rounded-3xl border border-[#00E5FF]/30 bg-[#00E5FF]/10"
          >
            <div className="grid lg:grid-cols-12">
              <div className="border-b border-[#00E5FF]/20 p-6 md:p-8 lg:col-span-7 lg:border-b-0 lg:border-r">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                  What Happens Next
                </div>

                <h2 className="mt-3 font-display text-3xl font-semibold">
                  We&apos;ll Take It From Here
                </h2>

                <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-300 md:text-base">
                  You&apos;re all set. We&apos;ll finish preparing your
                  TapToTable Page and make sure everything is ready before
                  sending your secure, easy-to-use TapToTable Dashboard access.
                </p>

                <div className="mt-7 space-y-6">
                  {nextSteps.map((step) => (
                    <div
                      key={step.number}
                      className="flex items-start gap-4"
                    >
                      <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF] text-sm font-bold text-black">
                        {step.number}
                      </span>

                      <div>
                        <h3 className="font-display text-lg font-semibold text-white">
                          {step.title}
                        </h3>

                        <p className="mt-1 text-sm leading-relaxed text-zinc-400">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 md:p-8 lg:col-span-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                  Your TapToTable Plan Includes
                </div>

                <h2 className="mt-3 font-display text-2xl font-semibold">
                  Everything You Need to Keep Your Page Current
                </h2>

                <ul className="mt-6 space-y-4">
                  {includedItems.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm leading-relaxed text-zinc-300"
                    >
                      <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF]/15 text-xs text-[#00E5FF]">
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 rounded-2xl border border-white/10 bg-black/20 p-5">
                  <div className="text-sm font-semibold text-white">
                    Watch your email
                  </div>

                  <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                    We&apos;ll send your secure, easy-to-use TapToTable Dashboard
                    access and any important setup information to the email
                    address you used during checkout. Please allow a little
                    time for final setup and check your spam or promotions
                    folder if needed.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.18 }}
            className="mt-6 rounded-3xl border border-violet-400/20 bg-violet-400/[0.05] p-6 md:p-8"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">
              Your Dashboard
            </div>

            <h2 className="mt-3 font-display text-2xl font-semibold">
              Promote What Matters Right Now
            </h2>

            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400 md:text-base">
              Use your secure, easy-to-use TapToTable Dashboard on your
              phone or computer to switch between Promotions, Events,
              and Featured Items. Create special offers, promote live
              music, tastings, watch parties, and holiday events, or
              showcase a signature menu item without requiring a
              discount or claim code.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.22 }}
            className="mt-6 grid gap-6 md:grid-cols-2"
          >
            <div className="rounded-3xl border border-white/10 bg-[#121214] p-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Need Assistance?
              </div>

              <h2 className="mt-3 font-display text-xl font-semibold">
                Questions? We&apos;re Here to Help.
              </h2>

              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                Support is included. Include your restaurant name and
                the email address used during checkout so we can locate
                your activation quickly.
              </p>

              <button
                type="button"
                onClick={() => setActiveInquiry("support")}
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                data-testid="activation-support-button"
              >
                Contact TapToTable Support
              </button>

              {activeInquiry === "support" && (
                <ActivationInquiryForm
                  requestType="support"
                  onClose={() => setActiveInquiry(null)}
                />
              )}

            </div>

            <div className="rounded-3xl border border-white/10 bg-[#121214] p-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Continue Exploring
              </div>

              <h2 className="mt-3 font-display text-xl font-semibold">
                Return to TapToTable
              </h2>

              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                Learn more about how TapToTable helps turn online interest
                into restaurant visits through Promotions, Events,
                Featured Items, and clear customer actions.
              </p>

              <Link
                to="/"
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-[#00E5FF] px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#66FFFF]"
                data-testid="return-home-button"
              >
                Return to TapToTable
              </Link>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
            className="mt-6 rounded-2xl border border-white/10 bg-[#121214] p-6"
          >
            <h2 className="font-display text-lg font-semibold text-white">
              Own Additional Restaurant Locations?
            </h2>

            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Your first restaurant location is $59/month. Each additional
              eligible location under the same ownership is $39/month,
              with its own TapToTable Page, login, dashboard, and
              separately arranged subscription.
            </p>

            <p className="mt-2 text-xs leading-relaxed text-zinc-500">
              Additional locations are not automatically activated or
              charged as part of your first-location subscription.
              Contact us to arrange them after ownership is confirmed.
            </p>


            <button
              type="button"
              onClick={() => setActiveInquiry("additional_locations")}
              className="mt-4 inline-flex items-center justify-center rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-4 py-3 text-sm font-semibold text-[#66FFFF] transition-colors hover:bg-[#00E5FF]/15"
            >
              Ask About Additional Locations
            </button>

            
            {activeInquiry === "additional_locations" && (
              <ActivationInquiryForm
                requestType="additional_locations"
                onClose={() => setActiveInquiry(null)}
              />
            )}

          </motion.section>

          <div className="mx-auto mt-8 max-w-2xl text-center">
            <p className="text-xs leading-relaxed text-zinc-500">
              Your first-location TapToTable plan is billed at $59 per month
              through Stripe and automatically renews each month until
              canceled. Any additional restaurant subscriptions are
              arranged and billed separately.
            </p>

            <p className="mt-3 text-sm text-zinc-400">
              Thanks for choosing TapToTable. We look forward to helping
              your restaurant turn more online interest into visits and
              customer activity.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
